import React, { useEffect, useRef, useState } from "react";
import { Box, Flex, Input, Text } from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { AppBar } from "../../components/mobileUi";
import {
  DesktopBack,
  DesktopPage,
  DesktopPanel,
  useIsDesktop,
} from "../../components/desktopUi";
import { Png } from "../../components/managementUi";
import SignaturePad from "../../components/SignaturePad";
import ConfirmDialog from "../../components/ConfirmDialog";
import { appImages } from "../../assets/appicons";
import { createSignatureApi } from "../../api/signature.api";
import { extractErrorMessage } from "../../api/client";
import { optimizeSignatureImageToBase64 } from "../../utils/signatureImage";
import { EMP_BASE_PATH } from "../../config";
import {
  SIGNATURE_CREATE_MODE_OPTIONS,
  SIGNATURE_CREATE_STATUS_OPTIONS,
  SIGNATURE_SOURCE_TYPE,
  SIGNATURE_STATUS,
} from "./signature.constant";

// ===== Màu lấy từ mobile-app-v2 (src/themes/colors/default.ts) =====
const C = {
  primary100: "#2168DA",
  primary200: "#4081EB",
  base100: "#FFFFFF",
  base200: "#E1E1E1",
  base600: "#C8C8C8",
  baseContent: "#000000",
  error: "#EC1515",
};

/* --------------------------------------------------- radio (khớp app) */
const RadioGroup = ({ options, value, onChange }) => (
  <Flex flexWrap="wrap" gap="20px">
    {options.map((o) => {
      const isActive = o.value === value;
      return (
        <Flex
          key={o.value}
          as="button"
          type="button"
          align="center"
          onClick={() => onChange(o.value)}
        >
          <Flex
            w="20px"
            h="20px"
            borderRadius="full"
            border="2px solid"
            borderColor={isActive ? C.primary100 : C.base600}
            align="center"
            justify="center"
            flexShrink={0}
          >
            {isActive ? (
              <Box w="10px" h="10px" borderRadius="full" bg={C.primary100} />
            ) : null}
          </Flex>
          <Box w="8px" />
          <Text fontSize="14px" color={C.baseContent}>
            {o.label}
          </Text>
        </Flex>
      );
    })}
  </Flex>
);

const FieldLabel = ({ text }) => (
  <Text fontSize="14px" fontWeight={600} color={C.baseContent} mb="8px">
    {text}
  </Text>
);

/* ============================================================== màn hình */
const SignatureCreate = () => {
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const padRef = useRef(null);
  const fileRef = useRef(null);

  const [title, setTitle] = useState("");
  const [status, setStatus] = useState(SIGNATURE_STATUS.ENABLE);
  const [mode, setMode] = useState(SIGNATURE_SOURCE_TYPE.DRAWN);
  const [pickedImageUrl, setPickedImageUrl] = useState(null);
  const [pickedImageBase64, setPickedImageBase64] = useState(null);
  // Hộp xác nhận trước khi tạo (khớp Alert.alert "Xác nhận tạo chữ kí" của app)
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(
    () => () => {
      if (pickedImageUrl) URL.revokeObjectURL(pickedImageUrl);
    },
    [pickedImageUrl]
  );

  const createMutation = useMutation({
    mutationFn: (body) => createSignatureApi(body),
    onSuccess: () => {
      toast.success("Tạo chữ kí thành công");
      queryClient.invalidateQueries({ queryKey: ["emp", "signature-list"] });
      navigate(`${EMP_BASE_PATH}/chu-ky`);
    },
    onError: (e) => toast.error(extractErrorMessage(e, "Tạo chữ kí thất bại")),
  });

  const pickImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      // Nén ảnh trước khi gửi để tránh đẩy payload quá nặng (khớp app)
      const base64 = await optimizeSignatureImageToBase64(file);
      if (pickedImageUrl) URL.revokeObjectURL(pickedImageUrl);
      setPickedImageUrl(URL.createObjectURL(file));
      setPickedImageBase64(base64);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Không xử lý được ảnh đã chọn"));
    }
  };

  const submit = () => {
    setConfirmOpen(false);

    if (!title.trim()) {
      toast.error("Vui lòng nhập tiêu đề chữ kí");
      return;
    }

    let signatureImageBase64 = null;

    if (mode === SIGNATURE_SOURCE_TYPE.DRAWN) {
      signatureImageBase64 = padRef.current?.getSignature() ?? null;
      if (!signatureImageBase64) {
        toast.error("Bạn chưa vẽ chữ kí");
        return;
      }
    } else {
      if (!pickedImageBase64) {
        toast.error("Vui lòng chọn ảnh chữ kí");
        return;
      }
      signatureImageBase64 = pickedImageBase64;
    }

    createMutation.mutate({
      title: title.trim(),
      status,
      sourceType: mode,
      signatureImageBase64,
    });
  };

  const confirmSubmit = () => setConfirmOpen(true);

  const pageBody = (
    <>

      <Box p="16px">
        {/* Tiêu đề */}
        <FieldLabel text="Tiêu đề chữ kí" />
        <Flex
          border="1px solid"
          borderColor={C.base200}
          borderRadius="8px"
          px="12px"
          h="44px"
          align="center"
        >
          <Input
            variant="unstyled"
            fontSize="14px"
            color={C.baseContent}
            placeholder="Nhập tiêu đề..."
            _placeholder={{ color: C.base600 }}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </Flex>

        <Box h="20px" />

        {/* Trạng thái */}
        <FieldLabel text="Trạng thái" />
        <RadioGroup
          options={SIGNATURE_CREATE_STATUS_OPTIONS}
          value={status}
          onChange={setStatus}
        />

        <Box h="20px" />

        {/* Loại chữ kí */}
        <FieldLabel text="Loại chữ kí" />
        <RadioGroup
          options={SIGNATURE_CREATE_MODE_OPTIONS}
          value={mode}
          onChange={setMode}
        />

        <Box h="20px" />

        {mode === SIGNATURE_SOURCE_TYPE.DRAWN ? (
          <>
            <Flex align="center" justify="space-between">
              <FieldLabel text="Vẽ chữ kí" />
              <Box
                as="button"
                type="button"
                title="Vẽ lại"
                mb="8px"
                onClick={() => padRef.current?.clear()}
              >
                <Png src={appImages.icon_delete_square} size={18} color={C.error} />
              </Box>
            </Flex>
            <SignaturePad ref={padRef} height={220} />
          </>
        ) : (
          <>
            <FieldLabel text="Upload ảnh chữ kí (Yêu cầu kí bằng bút đen trên giấy trắng A4)" />
            <Flex
              as="button"
              type="button"
              onClick={() => fileRef.current?.click()}
              align="center"
              alignSelf="flex-start"
              border="1px solid"
              borderColor={C.base200}
              borderRadius="8px"
              px="16px"
              h="40px"
              gap="8px"
            >
              <Png src={appImages.icon_plus} size={16} color={C.baseContent} />
              <Text fontSize="14px" fontWeight={500} color={C.baseContent}>
                Chọn ảnh
              </Text>
            </Flex>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              hidden
              onChange={pickImage}
            />

            {pickedImageUrl ? (
              <Box
                mt="12px"
                border="1px solid"
                borderColor={C.base200}
                borderRadius="8px"
                p="8px"
                bg={C.base100}
              >
                <img
                  src={pickedImageUrl}
                  alt="Ảnh chữ kí"
                  style={{ width: "100%", height: "180px", objectFit: "contain" }}
                />
              </Box>
            ) : null}
          </>
        )}

        <Box h="32px" />

        {/* Hoàn thành */}
        <Flex
          as="button"
          type="button"
          onClick={confirmSubmit}
          disabled={createMutation.isPending}
          w="100%"
          h="52px"
          borderRadius="8px"
          bg={C.primary200}
          align="center"
          justify="center"
          opacity={createMutation.isPending ? 0.6 : 1}
        >
          <Text fontSize="15px" fontWeight={700} color={C.base100}>
            Hoàn thành
          </Text>
        </Flex>
      </Box>

      <ConfirmDialog
        isOpen={confirmOpen}
        title="Xác nhận tạo chữ kí"
        description="Bạn có chắc chắn muốn tạo chữ kí này không?"
        confirmText="Xác nhận"
        cancelText="Hủy"
        loading={createMutation.isPending}
        onConfirm={submit}
        onClose={() => setConfirmOpen(false)}
      />
    </>
  );

  // ===================== Bản WEB (desktop) =====================
  if (isDesktop) {
    return (
      <DesktopPage title="Tạo chữ kí mới" right={<DesktopBack onClick={() => navigate(-1)} />}>
        <DesktopPanel p="0" pb="24px">
          {pageBody}
        </DesktopPanel>
      </DesktopPage>
    );
  }

  return (
    <Box maxW="480px" mx="auto" pb="40px">
      <AppBar title="Tạo chữ kí mới" />
      {pageBody}
    </Box>
  );
};

export default SignatureCreate;
